import { toast } from 'sonner';
import { supabase } from '../supabase/client';

const GCP_API_PROXY_URL = import.meta.env.VITE_GCP_API_PROXY_URL || 'https://api-proxy-613409150384.us-east1.run.app';

class AudioTranscriptionService {
  private initialized: boolean = true; // Always initialized since we use proxy

  constructor() {
    console.log('Audio Transcription Service initialized (using secure proxy)');
  }

  // Check if the service is initialized
  isInitialized(): boolean {
    return this.initialized;
  }

  // Transcribe audio using Gemini via secure proxy
  async transcribeAudio(audioBlob: Blob): Promise<string> {
    try {
      console.log('Transcribing audio via proxy:', audioBlob.type, audioBlob.size);

      // Get current session token for authentication
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('Authentication required for transcription service');
      }

      // Convert audio blob to base64
      const base64Data = await this._blobToBase64(audioBlob);

      // Get the MIME type from the blob
      const mimeType = audioBlob.type || 'audio/webm';

      // Call the proxy endpoint
      const response = await fetch(`${GCP_API_PROXY_URL}/gemini/transcribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          audio_base64: base64Data,
          mime_type: mimeType
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Gemini proxy error:', errorData);
        throw new Error(`Gemini proxy returned ${response.status}: ${JSON.stringify(errorData)}`);
      }

      const data = await response.json();
      const transcription = data.transcription || "Couldn't transcribe audio clearly";

      console.log('Transcription result:', transcription);
      return transcription;

    } catch (error) {
      console.error('Error transcribing audio:', error);
      throw error;
    }
  }

  // Convert a blob to base64
  private async _blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          // Remove the data URL prefix (e.g., "data:audio/webm;base64,")
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        } else {
          reject(new Error('Failed to convert blob to base64'));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}

// Create a singleton instance
export const audioTranscriptionService = new AudioTranscriptionService();
export default audioTranscriptionService;
